import { useEffect, useRef } from 'react';
import { Search, ScanLine } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { LeadCard } from '@/features/leads/ui/lead-card';
import type { Lead } from '@/shared/types';
import { OperatorRegisterInvite } from './operator-register-invite';

export type OperatorSearchStatus = 'idle' | 'loading' | 'found' | 'empty';

interface OperatorSearchBarProps {
  query: string;
  onQueryChange: (value: string) => void;
  placeholder: string;
  loading?: boolean;
  status: OperatorSearchStatus;
  results: Lead[];
  onSelect: (lead: Lead) => void;
  onScanClick: () => void;
  onRestartSearch: () => void;
  idPassbook: string;
  labelQtde: string;
  isCashback: boolean;
  uniqueIdLabel: string;
  shortUrl?: string;
  organizationName?: string;
}

export function OperatorSearchBar({
  query,
  onQueryChange,
  placeholder,
  loading,
  status,
  results,
  onSelect,
  onScanClick,
  onRestartSearch,
  idPassbook,
  labelQtde,
  isCashback,
  uniqueIdLabel,
  shortUrl,
  organizationName,
}: OperatorSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const showResults = status === 'found' && results.length > 0;
  const showEmpty = status === 'empty';
  const showLoading = status === 'loading' || Boolean(loading && query.trim().length >= 2);
  const fillHeight = showResults || showEmpty || showLoading;

  useEffect(() => {
    if (!showEmpty) return;
    const t = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [showEmpty]);

  const handleRestart = () => {
    onRestartSearch();
    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  return (
    <div className={`flex min-h-0 flex-col space-y-4 ${fillHeight ? 'flex-1' : 'shrink-0'}`}>
      <div className="flex shrink-0 gap-2 p-0.5">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={placeholder}
            className="h-11 rounded-2xl pl-10 text-base"
            autoComplete="off"
            inputMode="search"
            aria-describedby={showEmpty ? 'operator-search-empty' : undefined}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          className="h-11 shrink-0 gap-2 rounded-2xl px-4"
          onClick={onScanClick}
          aria-label="Escanear QR"
        >
          <ScanLine className="size-5" />
          <span className="hidden sm:inline">QR</span>
        </Button>
      </div>

      {showLoading ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2" aria-busy="true" aria-label="Buscando clientes">
          <div className="flex shrink-0 items-center justify-between gap-2 px-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Buscando…
            </p>
          </div>
          <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-1 py-1.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[72px] rounded-2xl" />
            ))}
          </div>
        </div>
      ) : null}

      {showEmpty && !showLoading ? (
        <OperatorRegisterInvite
          idPassbook={idPassbook}
          shortUrl={shortUrl}
          organizationName={organizationName}
          query={query}
          uniqueIdLabel={uniqueIdLabel}
          onRestart={handleRestart}
        />
      ) : null}

      {showResults && !showLoading ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex shrink-0 items-center justify-between gap-2 px-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Resultados
            </p>
            <p className="text-xs tabular-nums text-muted-foreground">
              {results.length} {results.length === 1 ? 'cliente' : 'clientes'}
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <ul className="space-y-2.5 px-1 py-1.5 pb-3">
              {results.map((lead, index) => (
                <li key={lead._id} className="list-none">
                  <LeadCard
                    lead={lead}
                    index={index}
                    selected={false}
                    onToggle={() => undefined}
                    onEdit={() => undefined}
                    onPick={onSelect}
                    idPassbook={idPassbook}
                    labelQtde={labelQtde}
                    isCashback={isCashback}
                    canSeeEmail={false}
                    hasMultiStore={false}
                    hideSelection
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}
